// @flow
import zentakeApi from 'api'
import ModalPrompt from 'components/Modal/ModalPrompt'
import Spinner from 'components/Spinner'
import ModalSendReminder from 'features/sendReminder/components/ModalSendReminder'
import usePermissions from 'hooks/usePermissions'
import useScreenSizeStyle from 'hooks/useScreenSizeStyle'
import filter from 'lodash/filter'
import isNil from 'lodash/isNil'
import { useUpdateResponseQueue } from 'queries/response_queue/useUpdateResponseQueue'
import * as React from 'react'
import { toast } from 'react-toastify'
import type { Message } from 'types/message'
import FlatCard from 'ui/atoms/FlatCard'
import Flex from 'ui/atoms/Flex'
import FlexBox from 'ui/atoms/FlexBox'
import Table from 'ui/atoms/Table'
import Text from 'ui/atoms/Text'
import TableFooterButton from 'ui/table/TableFooterButton'
import { DELIVERY_METHODS } from 'utils/constants'
import { handleSearch, isFilledArray } from 'utils/helpers'
import { emailSchema, phoneSchema } from 'utils/schemas'
import { ZentakeContext } from 'utils/ZentakeContext'

import MessageRow from '../dashboard/MessageRow'
import ResponseRow from '../dashboard/ResponseRow'

type Props = {
  searchTerm: string,
}

const PROMPT_CB_TYPE = {
  INVALID_EMAIL: 'invalid_email',
  INVALID_SMS: 'invalid_sms',
  RESEND_EMAIL: 'resend_email',
  RESEND_SMS: 'resend_sms',
}

const PROMPT_STRINGS = {
  INVALID_EMAIL: {
    type: PROMPT_CB_TYPE.INVALID_EMAIL,
    title: 'Update email address',
    description: 'Enter new email address to re-send',
    submitTitle: 'Update email',
    inputPlaceholder: 'New email address',
  },
  INVALID_SMS: {
    type: PROMPT_CB_TYPE.INVALID_SMS,
    title: 'Update phone number',
    description: 'Enter new phone number to re-send',
    submitTitle: 'Update phone number',
    inputPlaceholder: 'New phone number',
  },
  RESEND_EMAIL: {
    type: PROMPT_CB_TYPE.RESEND_EMAIL,
    title: 'Send reminder email',
    description: 'Update the email address to send a reminder',
    submitTitle: 'Send reminder',
    inputPlaceholder: 'Email address',
  },
  RESEND_SMS: {
    type: PROMPT_CB_TYPE.RESEND_SMS,
    title: 'Send reminder SMS',
    description: 'Update the phone number to send a reminder',
    submitTitle: 'Send reminder',
    inputPlaceholder: 'Phone number',
  },
}

function renderLoadingComponent() {
  return (
    <FlatCard>
      <Flex $flexDirection="row" justify="center">
        <Spinner className="mr-2" color="onLight" size={24} />
        <Text $small>Loading</Text>
      </Flex>
    </FlatCard>
  )
}

function DashboardTables({ searchTerm }: Props): React.Node {
  const {
    messages,
    fetchingMessages,
    setMessages,
    reloadMessages,
    hasMoreMessages,
    getMoreMessages,
    responses,
    fetchingResponses,
    setResponses,
    hasMoreResponses,
    getMoreResponses,
    teamDetails,
  } = React.useContext(ZentakeContext)
  const { updateResponseQueue } = useUpdateResponseQueue()

  const [, setError] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [responseLoad, setResponseLoad] = React.useState(true)
  const [removingResponse, setRemovingResponse] = React.useState(null)
  const [removingMessage, setRemovingMessage] = React.useState(null)

  const [filteredMessages, setFilteredMessages] = React.useState(messages)
  const [filteredResponses, setFilteredResponses] = React.useState(messages)

  const [promptData, setPromptData] = React.useState<Message | null>(null)
  const [promptStrings, setPromptStrings] = React.useState(
    PROMPT_STRINGS.RESEND_EMAIL
  )
  const [hasPrompt, setHasPrompt] = React.useState(false)
  const [isPromptBusy, setPromptBusy] = React.useState(false)

  const spinnerSize = useScreenSizeStyle({ md: '24', generic: '18' })

  const [isSendReminder, setIsSendReminder] = React.useState(false)

  React.useEffect(() => {
    const sorted = handleSearch(searchTerm, messages, [
      ['customer', 'first_name'],
      ['customer', 'last_name'],
      ['form', 'name'],
      ['packet', 'name'],
    ])
    setFilteredMessages(sorted)
  }, [messages, searchTerm])

  React.useEffect(() => {
    const sorted = handleSearch(searchTerm, responses, [
      ['customer', 'first_name'],
      ['customer', 'last_name'],
      ['form', 'name'],
      ['packet', 'name'],
    ])
    setFilteredResponses(sorted)
  }, [responses, searchTerm])

  React.useEffect(() => {
    if (messages === null) {
      setLoading(true)
    } else {
      setLoading(false)
    }
  }, [messages])

  React.useEffect(() => {
    if (responses === null) {
      setResponseLoad(true)
    } else {
      setResponseLoad(false)
    }
  }, [responses])

  const archiveResponse = async (responseId: string, isPacket: boolean) => {
    try {
      setRemovingResponse(responseId)
      isPacket
        ? await zentakeApi.archivePacketResponse(responseId)
        : await zentakeApi.archiveResponse(responseId)
      const responseKey = isPacket ? 'packet_response' : 'response'
      const filteredResponses = filter(
        responses,
        (r) => r?.[responseKey]?.id !== responseId
      )
      setResponses(filteredResponses)
      setRemovingResponse(null)
      toast('Response archived')
    } catch (error) {
      setRemovingResponse(null)
      setError(error)
    }
  }

  const archiveResponseFromMessage = async (
    messageId: string,
    responseId: string,
    isPacket: boolean
  ) => {
    try {
      setRemovingMessage(messageId)
      isPacket
        ? await zentakeApi.archivePacketResponse(responseId)
        : await zentakeApi.archiveResponse(responseId)
      const filteredMessages = filter(messages, (m) => m.id !== messageId)
      setMessages(filteredMessages)
      setRemovingMessage(null)
      toast('Response archived')
    } catch (error) {
      setRemovingMessage(null)
      setError(error)
    }
  }

  const deleteMessage = React.useCallback(
    async (messageId, isPacket, responseId) => {
      try {
        setRemovingMessage(messageId)
        // remove response
        isPacket
          ? await zentakeApi.deletePacketResponse(responseId)
          : await zentakeApi.deleteResponse(responseId)
        // remove message
        await zentakeApi.deleteMessage(messageId)
        const filteredMessages = filter(messages, (m) => m.id !== messageId)
        setMessages(filteredMessages)
        setRemovingMessage(null)
        toast('Item successfully deleted')
      } catch (error) {
        setRemovingMessage(null)
        setError(error)
      }
    },
    [messages, setError, setMessages]
  )

  const onUpdateResponseQueue = React.useCallback(
    async (data) => {
      try {
        await updateResponseQueue(data)
        reloadMessages()
        toast.success('Queue Updated')
      } catch (error) {
        toast.error('Failed to update response queue')
        setError(error)
      }
    },
    [reloadMessages, updateResponseQueue]
  )

  const onSendMessageReminder = React.useCallback(async (selectedMessage) => {
    setPromptData(selectedMessage)
    setPromptStrings(
      selectedMessage?.preferred_delivery_method === DELIVERY_METHODS.SMS
        ? PROMPT_STRINGS.RESEND_SMS
        : PROMPT_STRINGS.RESEND_EMAIL
    )
    setIsSendReminder(true)
  }, [])

  function setFormToUpdateEmail(selectedMessage: Message) {
    setPromptData(selectedMessage)
    setPromptStrings(
      selectedMessage.preferred_delivery_method === 'sms'
        ? PROMPT_STRINGS.INVALID_SMS
        : PROMPT_STRINGS.INVALID_EMAIL
    )
    setHasPrompt(true)
  }

  // Recreate the message and delete the previous, updating the email
  const submitNewMessageEmail = React.useCallback(
    async (newEmail) => {
      if (!promptData) return

      try {
        setPromptBusy(true)
        // Send a new message using the new email
        const newMessage = await zentakeApi.sendMessageToCustomer({
          targetEmail: newEmail,
          customer: promptData.customer.id,
          preferredDeliveryMethod: 'email',
          deliveryMethodUsed: 'email',
          messageType: promptData.message_type,
          formId: promptData?.response?.form?.id,
          packetId: promptData?.packet_response?.packet?.id,
          is_waiting: false,
          team: teamDetails?.uid,
        })
        // Delete previous message
        const isPacket = !isNil(promptData?.packet_response?.packet?.id)
        isPacket
          ? await zentakeApi.deletePacketResponse(
              promptData?.packet_response?.id
            )
          : await zentakeApi.deleteResponse(promptData.response?.id)
        const deletedPreviousMessage = await zentakeApi.deleteMessage(
          promptData.id
        )
        if (deletedPreviousMessage) {
          const newMessages = messages.filter((m) => m.id !== promptData.id)
          setMessages([newMessage, ...newMessages])
        } else {
          setMessages([newMessage, ...messages])
        }
        // Update user email
        promptData?.customer?.id &&
          (await zentakeApi.updateClient(promptData.customer.id, {
            email: newEmail,
          }))
        setPromptBusy(false)
        closeUpdateEmailPrompt()
      } catch (error) {
        setPromptBusy(false)
      }
    },
    [messages, promptData, setMessages, teamDetails]
  )

  // Recreate the message and delete the previous, updating the phone number
  const submitNewPhoneNumber = React.useCallback(
    async (newPhone) => {
      if (!promptData) return
      try {
        setPromptBusy(true)
        // Send a new message using the new email
        const newMessage = await zentakeApi.sendMessageToCustomer({
          targetEmail: promptData.target_email,
          customer: promptData.customer.id,
          preferredDeliveryMethod: DELIVERY_METHODS.SMS,
          deliveryMethodUsed: DELIVERY_METHODS.SMS,
          messageType: promptData.message_type,
          formId: promptData.response?.form?.id,
          packetId: promptData.packet_response?.packet?.id,
          is_waiting: false,
          team: teamDetails?.uid,
          phoneNumber: newPhone,
        })
        // Delete previous message
        const isPacket = !isNil(promptData?.packet_response?.id)
        isPacket
          ? await zentakeApi.deletePacketResponse(
              promptData.packet_response?.id
            )
          : await zentakeApi.deleteResponse(promptData.response?.id)
        await zentakeApi.deleteMessage(promptData.id)
        const newMessages = messages.filter((m) => m.id !== promptData.id)
        setMessages([newMessage, ...newMessages])
        // Update user email
        if (!isNil(promptData?.customer?.id)) {
          await zentakeApi.updateClient(promptData.customer.id, {
            phone_number: newPhone,
          })
        }
        setPromptBusy(false)
        closeUpdateEmailPrompt()
      } catch (error) {
        setPromptBusy(false)
      }
    },
    [messages, promptData, setMessages, teamDetails]
  )

  const sendReminder = React.useCallback(
    async (newEmailOrPhone, deliveryType) => {
      if (!promptData) return

      try {
        setPromptBusy(true)

        // send reminder to user
        await zentakeApi.sendMessageReminder(
          promptData.id,
          newEmailOrPhone,
          deliveryType
        )

        // Update user email
        promptData?.customer?.id &&
          (await zentakeApi.updateClient(
            promptData.customer.id,
            deliveryType === DELIVERY_METHODS.SMS
              ? {
                  phone_number: newEmailOrPhone,
                }
              : {
                  email: newEmailOrPhone,
                }
          ))

        setPromptBusy(false)
        reloadMessages()
        closeUpdateEmailPrompt()
      } catch (error) {
        setPromptBusy(false)
      }
    },
    [promptData, reloadMessages]
  )

  function closeUpdateEmailPrompt() {
    setPromptData(null)
    setHasPrompt(false)
  }

  const onPromptModalSubmit = React.useCallback(
    (newEmailOrPhone) => {
      switch (promptStrings.type) {
        case PROMPT_CB_TYPE.INVALID_EMAIL:
          return submitNewMessageEmail(newEmailOrPhone)
        case PROMPT_CB_TYPE.INVALID_SMS:
          return submitNewPhoneNumber(newEmailOrPhone)
        case PROMPT_CB_TYPE.RESEND_EMAIL:
          return sendReminder(newEmailOrPhone, DELIVERY_METHODS.EMAIL)
        case PROMPT_CB_TYPE.RESEND_SMS:
          return sendReminder(newEmailOrPhone, DELIVERY_METHODS.SMS)
        default:
          return closeUpdateEmailPrompt()
      }
    },
    [promptStrings, sendReminder, submitNewMessageEmail, submitNewPhoneNumber]
  )

  const compact = useScreenSizeStyle({ md: false, generic: true })

  const renderEmptyState = React.useCallback(
    (items) => {
      if (loading || isFilledArray(items)) return null

      return (
        <Table.Row>
          <Table.Column colSpan={4}>
            {/* <Text $light>Nothing to show.</Text> */}
          </Table.Column>
        </Table.Row>
      )
    },
    [loading]
  )

  const { canViewResponse } = usePermissions()

  return (
    <div>
      <Flex
        $flexDirection={{
          default: 'col',
          lg: 'row',
        }}
        $horizontalSpacing={{ default: 'none', lg: 'medium' }}
        $verticalSpacing={{ default: 'medium', lg: 'none' }}
      >
        <FlexBox>
          <Table.Title $compact={compact}>
            Pending Items
            {fetchingMessages && (
              <Spinner className="ml-2" color="onLight" size={spinnerSize} />
            )}
          </Table.Title>
          <Table>
            <Table.Head
              columns={['Client', 'Form', 'Sent', '']}
              compact={compact}
            />
            <Table.Body>
              {renderEmptyState(filteredMessages)}
              {!loading && (
                <>
                  {isFilledArray(filteredMessages) &&
                    filteredMessages.map((message) => (
                      <MessageRow
                        archiveResponse={archiveResponseFromMessage}
                        deleteMessage={deleteMessage}
                        fetchingMessages={fetchingMessages}
                        key={String(message.id)}
                        message={message}
                        onSendReminder={onSendMessageReminder}
                        onUpdateEmailPress={setFormToUpdateEmail}
                        removingMessage={removingMessage}
                        updateResponseQueue={onUpdateResponseQueue}
                      />
                    ))}
                  {hasMoreMessages && canViewResponse && (
                    <tr>
                      <td colSpan={4}>
                        <TableFooterButton
                          isLoading={fetchingMessages}
                          label="Load more"
                          labelLoading="Loading"
                          onClick={getMoreMessages}
                        />
                      </td>
                    </tr>
                  )}
                </>
              )}
            </Table.Body>
          </Table>
          {loading && renderLoadingComponent()}
        </FlexBox>
        <FlexBox>
          <Table.Title $compact={compact}>
            Received Items
            {fetchingResponses && (
              <Spinner className="ml-2" color="onLight" size={spinnerSize} />
            )}
          </Table.Title>
          <Table>
            <Table.Head
              columns={['Client', 'Form', 'Received', '']}
              compact={compact}
            />
            <Table.Body>
              {renderEmptyState(filteredResponses)}
              {!responseLoad && (
                <>
                  {isFilledArray(filteredResponses) &&
                    filteredResponses.map((forms) => (
                      <ResponseRow
                        archiveResponse={archiveResponse}
                        fetchingResponses={fetchingResponses}
                        key={String(forms.id)}
                        removingResponse={removingResponse}
                        response={forms}
                      />
                    ))}
                  {hasMoreResponses && canViewResponse && (
                    <tr>
                      <td colSpan={4}>
                        <TableFooterButton
                          isLoading={fetchingResponses}
                          label="Load more"
                          labelLoading="Loading"
                          onClick={getMoreResponses}
                        />
                      </td>
                    </tr>
                  )}
                </>
              )}
            </Table.Body>
          </Table>
        </FlexBox>
      </Flex>
      {responseLoad && renderLoadingComponent()}
      {hasPrompt && (
        <ModalPrompt
          canDismiss
          description={promptStrings.description}
          initialInput={
            promptData?.preferred_delivery_method === DELIVERY_METHODS.SMS
              ? promptData?.target_phone || ''
              : promptData?.target_email || ''
          }
          inputPlaceholder={promptStrings.inputPlaceholder}
          inputScheme={
            promptData?.preferred_delivery_method === DELIVERY_METHODS.SMS
              ? phoneSchema
              : emailSchema
          }
          inputType={
            promptData?.preferred_delivery_method === DELIVERY_METHODS.SMS
              ? 'phone'
              : 'email'
          }
          isVisible={hasPrompt}
          loading={isPromptBusy}
          onClose={closeUpdateEmailPrompt}
          onSubmit={onPromptModalSubmit}
          submitTitle={promptStrings.submitTitle}
          title={promptStrings.title}
        />
      )}
      {isSendReminder && promptData && (
        <ModalSendReminder
          clientName={`${promptData?.customer?.first_name || ''} ${
            promptData?.customer?.last_name || ''
          }`}
          email={promptData?.customer?.email || promptData?.target_email || ''}
          isVisible={isSendReminder}
          message={promptData}
          onClose={() => {
            reloadMessages()
            setIsSendReminder(false)
          }}
          phone={
            promptData?.customer?.phone_number || promptData?.target_phone || ''
          }
          preferredDeliveryMethod={promptData?.preferred_delivery_method}
        />
      )}
    </div>
  )
}

export default DashboardTables
